import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { parseCaeXmlCfeTypeFromString } from "@/lib/parseCaeXmlCfeType";
import { caeKeys, tenantKeys } from "@/lib/queryKeys";
import { addCae } from "@/services/invoiceService";
import { addCaeForTenant } from "@/services/platformTenantsService";

/**
 * CFE types whose CAE can be uploaded here. The backend imposes no whitelist —
 * it reads TCFE straight from the file — so this list is the only thing that
 * decides what an operator can register, and 182 being absent made a valid
 * e-Resguardo CAE unloadable through the product at all.
 *
 * 182 is deliberately NOT in REQUIRED_ONBOARDING_CAE_CFE_TYPES: only the
 * proveedor issuing 80 UI benefit resguardos (Decreto 206/019) needs one, so
 * requiring it would block every ordinary tenant's onboarding.
 */
const CAE_CFE_TYPES = ["101", "102", "111", "112", "182"] as const;

export interface AddCaeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, adds CAE for this tenant via platform API (support view). */
  tenantId?: string;
}

export function AddCaeDialog({
  open,
  onOpenChange,
  tenantId,
}: AddCaeDialogProps): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [cfeType, setCfeType] = useState<string>("");
  const [cfeTypeAutoDetected, setCfeTypeAutoDetected] = useState(false);
  const [detectingCfeType, setDetectingCfeType] = useState(false);
  const [cfeTypeParseHint, setCfeTypeParseHint] = useState<string | null>(null);
  const [isNew, setIsNew] = useState<boolean>(true);
  const [lastUsedNumber, setLastUsedNumber] = useState<string>("");
  const [fileInputKey, setFileInputKey] = useState(0);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setCfeType("");
    setCfeTypeAutoDetected(false);
    setDetectingCfeType(false);
    setCfeTypeParseHint(null);
    setIsNew(true);
    setLastUsedNumber("");
    setFileInputKey((k) => k + 1);
  }, [open]);

  const mutation = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error("File required");
      const last =
        lastUsedNumber.trim() === ""
          ? undefined
          : parseInt(lastUsedNumber.trim(), 10);
      if (last !== undefined && Number.isNaN(last)) {
        throw new Error("Invalid last used number");
      }
      const typeVal = cfeType.trim() || undefined;
      if (tenantId) {
        return addCaeForTenant(tenantId, file, isNew, last, typeVal);
      }
      return addCae(file, isNew, last, typeVal);
    },
    onSuccess: async () => {
      setFile(null);
      setCfeType("");
      setCfeTypeAutoDetected(false);
      setCfeTypeParseHint(null);
      setIsNew(true);
      setLastUsedNumber("");
      onOpenChange(false);
      if (tenantId) {
        await queryClient.invalidateQueries({
          queryKey: tenantKeys.cae(tenantId),
        });
        await queryClient.refetchQueries({
          queryKey: tenantKeys.cae(tenantId),
          type: "active",
        });
      } else {
        await queryClient.refetchQueries({
          queryKey: caeKeys.list(),
          type: "active",
        });
      }
      toast.success(t("settings.cae.addSuccess"));
    },
    onError: (err: Error & { response?: { data?: { message?: string } } }) => {
      const msg =
        err.response?.data?.message ??
        (err.message || t("settings.cae.addError"));
      if (
        typeof msg === "string" &&
        (msg.includes("RUC") || msg.includes("ruc") || msg.includes("company"))
      ) {
        toast.error(t("settings.cae.errorRucMismatch"));
      } else if (
        typeof msg === "string" &&
        (msg.includes("XML") || msg.includes("parse") || msg.includes("Invalid"))
      ) {
        toast.error(t("settings.cae.errorParse"));
      } else if (
        typeof msg === "string" &&
        (msg.includes("type") && (msg.includes("match") || msg.includes("coincide")))
      ) {
        toast.error(t("settings.cae.errorTypeMismatch"));
      } else {
        toast.error(msg);
      }
    },
  });

  const handleClose = (): void => {
    if (!mutation.isPending) {
      setFile(null);
      setCfeType("");
      setCfeTypeAutoDetected(false);
      setCfeTypeParseHint(null);
      setIsNew(true);
      setLastUsedNumber("");
      onOpenChange(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const next = e.target.files?.[0] ?? null;
    setFile(next);
    setCfeType("");
    setCfeTypeAutoDetected(false);
    setCfeTypeParseHint(null);
    if (!next) {
      return;
    }
    setDetectingCfeType(true);
    void next
      .text()
      .then((xml) => {
        const tcfe = parseCaeXmlCfeTypeFromString(xml);
        if (
          tcfe &&
          CAE_CFE_TYPES.includes(tcfe as (typeof CAE_CFE_TYPES)[number])
        ) {
          setCfeType(tcfe);
          setCfeTypeAutoDetected(true);
          setCfeTypeParseHint(null);
          return;
        }
        if (tcfe) {
          setCfeTypeParseHint(
            t("settings.cae.cfeTypeUnsupportedFromFile", { code: tcfe })
          );
          return;
        }
        setCfeTypeParseHint(t("settings.cae.cfeTypeDetectFailed"));
      })
      .catch(() => {
        setCfeTypeParseHint(t("settings.cae.cfeTypeDetectFailed"));
      })
      .finally(() => {
        setDetectingCfeType(false);
      });
  };

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!file) {
      toast.error(t("settings.cae.addError"));
      return;
    }
    if (!cfeType || !CAE_CFE_TYPES.includes(cfeType as (typeof CAE_CFE_TYPES)[number])) {
      toast.error(t("settings.cae.cfeTypeRequired"));
      return;
    }
    mutation.mutate();
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("settings.cae.addDialogTitle")}</DialogTitle>
          <DialogDescription>
            {t("settings.cae.addDialogUploadHint")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="cae-file">{t("settings.cae.fileLabel")}</Label>
            <Input
              key={fileInputKey}
              id="cae-file"
              type="file"
              accept=".xml"
              onChange={handleFileChange}
              disabled={mutation.isPending || detectingCfeType}
            />
            {file ? (
              <p className="truncate text-xs text-muted-foreground" title={file.name}>
                {file.name}
              </p>
            ) : null}
          </div>
          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Label htmlFor="cae-type">{t("settings.cae.cfeTypeSelectLabel")}</Label>
              {detectingCfeType ? (
                <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Loader2 className="size-3.5 animate-spin" aria-hidden />
                  {t("settings.cae.cfeTypeDetecting")}
                </span>
              ) : cfeTypeAutoDetected && cfeType ? (
                <span className="text-xs font-medium text-primary">
                  {t("settings.cae.cfeTypeDetectedFromFile")}
                </span>
              ) : null}
            </div>
            <Select
              value={cfeType}
              onValueChange={(v) => {
                setCfeType(v);
                setCfeTypeAutoDetected(false);
                setCfeTypeParseHint(null);
              }}
              disabled={detectingCfeType || mutation.isPending}
            >
              <SelectTrigger id="cae-type" className="transition-colors duration-150">
                <SelectValue placeholder={t("settings.cae.cfeTypeSelectPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {CAE_CFE_TYPES.map((code) => (
                  <SelectItem key={code} value={code}>
                    {code} – {t(`invoices.cfeTypes.${code}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {cfeTypeParseHint ? (
              <p className="text-xs text-amber-800 dark:text-amber-200/90">
                {cfeTypeParseHint}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                {t("settings.cae.cfeTypeSelectHint")}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label>{t("settings.cae.newVsNew")} / {t("settings.cae.newVsExisting")}</Label>
            <RadioGroup
              value={isNew ? "new" : "existing"}
              onValueChange={(v) => setIsNew(v === "new")}
              className="flex flex-col gap-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="new" id="cae-new" />
                <Label htmlFor="cae-new" className="font-normal cursor-pointer">
                  {t("settings.cae.newVsNew")}
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="existing" id="cae-existing" />
                <Label htmlFor="cae-existing" className="font-normal cursor-pointer">
                  {t("settings.cae.newVsExisting")}
                </Label>
              </div>
            </RadioGroup>
          </div>
          {!isNew && (
            <div className="space-y-1.5">
              <Label htmlFor="cae-last-used">{t("settings.cae.lastUsedNumber")}</Label>
              <Input
                id="cae-last-used"
                type="number"
                min={1}
                value={lastUsedNumber}
                onChange={(e) => setLastUsedNumber(e.target.value)}
                placeholder="ej. 12"
              />
              <p className="text-xs text-muted-foreground">
                {t("settings.cae.lastUsedNumberHint")}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleClose()}
              disabled={mutation.isPending}
            >
              {t("settings.cae.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={
                !file ||
                !cfeType ||
                mutation.isPending ||
                detectingCfeType
              }
            >
              {mutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("settings.cae.addButton")}
                </>
              ) : (
                t("settings.cae.addButton")
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
